import { Location } from '@angular/common';

import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog, MatMenuTrigger } from '@angular/material';
import {
  MatSnackBar,
  MatSnackBarConfig,
  MatSnackBarRef,
  SimpleSnackBar
} from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslocoService } from '@ngneat/transloco';
import { ApolloError } from 'apollo-client';
import { Logger } from 'aws-amplify';
import { fabric } from 'fabric';
import { cloneDeep } from 'lodash-es';
import { untilDestroyed } from 'ngx-take-until-destroy';
import { combineLatest, EMPTY, merge, Observable, of, ReplaySubject, Subject } from 'rxjs';
import {
  concatMap,
  debounceTime,
  distinctUntilChanged,
  filter,
  finalize,
  map,
  mergeMap,
  shareReplay,
  skip,
  startWith,
  switchMap,
  take,
  tap
} from 'rxjs/operators';
import { CopyToEmrModalComponent } from 'src/app/copy-to-emr-modal/copy-to-emr-modal.component';
import { SignImageWizardModalComponent } from 'src/app/encounters/signs/sign-image-wizard/sign-image-wizard-modal/sign-image-wizard-modal.component';
import { QrCodeModalComponent } from 'src/app/shared/qr-code-modal/qr-code-modal.component';
import { RealtimeMediaModalComponent } from 'src/app/shared/consult-forms/realtime-media-modal/realtime-media-modal.component';
import { HoverMenuHelper } from 'src/app/shared/util/hover-menu.util';
import { Clinic, ClinicService } from 'src/app/core/api/clinic.service';
import { QueueFaxResult } from 'src/app/core/api/fax.service';
import { Html2PdfService } from 'src/app/core/api/html-2-pdf.service';
import { AssessmentPdfSendService } from 'src/app/shared/dry-eye-pdf-export/assessment-pdf-send.service';
import { NavigationService } from 'src/app/core/api/navigation.service';
import { PatientService } from 'src/app/core/api/patient.service';
import {
  QuestionnaireRequest,
  QuestionnaireRequestService
} from 'src/app/core/api/questionnaire-request.service';
import { assessmentSideNavSchema } from 'src/app/core/schemas/navigation-schema';
import { AppStateService } from 'src/app/core/services/app-state.service';
import { StaffPermissionsService } from 'src/app/core/services/staff-permissions.service';
import {
  APIResponse,
  AssessmentBody,
  LogEntry
} from 'src/app/econsult/assessment-body/assessment-body.model';
import { PampIframeService } from 'src/app/core/services/pamp-iframe.service';
import { SubscriptionPlanAccessService } from 'src/app/core/services/subscription-plan-access.service';
import { AccessState, Feature } from 'src/app/core/schemas/subscription-plan.schema';
import { ClinicSetupService } from 'src/app/logged-in-navbar/clinic-setup-modal/clinic-setup.service';
import { LayoutsService } from 'src/app/logged-in-navbar/clinic-setup-modal/layouts/layouts.service';
import { ConfirmModalService } from 'src/app/shared/confirm-modal/confirm-modal.service';
import { PdfCustomExportComponent } from 'src/app/shared/dry-eye-pdf-export/pdf-custom-export/pdf-custom-export.component';
import { PdfSummaryService } from 'src/app/shared/dry-eye-pdf-export/pdf-summary-export/pdf-summary.service';
import { PhonePipe } from 'src/app/shared/shared-pipes/phone.pipe';
import { hasMissingEnrollmentFields } from 'src/app/patients/patient-enrollment.util';
import { safeParseJson } from 'src/app/shared/util/helper.util';
import { SchemaService } from 'src/app/shared/symptoms/services/schema/schema.service';
import { SymptomModes } from 'src/app/shared/symptoms/symptom.model';
import { FabricService } from 'src/app/signature/fabric.service';
import { InteractionSignoffComponent } from 'src/app/signature/interaction-signoff/interaction-signoff.component';
import { InteractionSignOffGroup } from 'src/app/signature/interaction-signoff/interaction-signoff.model';
import {
  AssessmentRequestType,
  AssessmentType,
  CognitoGroup,
  Doctor,
  PatientPortalGrantExportDestination,
  SRFaxStatus,
  StaffType
} from '../../../API';
import { AssessmentUtils } from '../../core/api/assessment-utils';
import {
  Assessment,
  AssessmentRequest,
  AssessmentService
} from '../../core/api/assessment.service';
import { ErrorHandlerService } from '../../core/api/error-handler.service';
import { Patient } from '../../core/api/patient.service';
import { StaffService } from '../../core/api/staff.service';
import { EConsultFormGroup, EntryMethod } from '../../econsult/econsult-form.model';
import { ChooseDoctorFromClinicComponent } from '../../shared/choose-doctor-from-clinic/choose-doctor-from-clinic.component';
import { TreatmentFiltersCDSService } from '../../shared/consult-forms/dry-eye-forms/dry-eye-assessment-form/treatment-filters/treatment-filters-cds.service';
import { PreOpService } from '../../shared/consult-forms/dry-eye-forms/pre-op/pre-op.service';
import { SymptomValidatorsService } from '../../shared/consult-forms/validators/symptom-validators/symptom-validators.service';
import {
  ButtonAppearances,
  ExportDestination,
  ExportDestinations
} from '../../shared/dry-eye-pdf-export/pdf-export-button/pdf-export-button.component';
import { PDFExportService } from '../../shared/dry-eye-pdf-export/pdf-export.service';
import { FormErrorScrollingService } from '../../shared/form-error-scrolling/form-error-scrolling.service';
import { ComponentCanDeactivate } from '../../shared/guards/save-data/component-can-deactivate';
import { AssessmentFormService } from '../assessment-form.service';
import { EncountersOutletName, EncountersRoutes } from '../encounters.routes';
import { CreateReferralInput, Priority } from './../../../API';
import { ReferralFormService } from './../../referral/referral-form/referral-form.service';
import { ReferralService } from './../../referral/referral.service';
import { PDFType } from './../../shared/dry-eye-pdf-export/dry-eye-pdf-export.component';
import { CustomPdfExportService } from './../../shared/dry-eye-pdf-export/pdf-custom-export/custom-pdf-export.service';
import { AssessmentSection } from './assessment-sidebar/assessment-sidebar.component';
import {
  ASSESSMENT_SECTION_SCHEMAS,
  NUTRITION_BASED_TESTING_SECTION_KEY
} from './assessments.model';
import {
  DryEyeMedicalHistoryFormGroupValue,
  MedicalHistorySummarySnapshot
} from 'src/app/shared/consult-forms/dry-eye-forms/dry-eye-medical-history-form/dry-eye-medical-history-form.model';
import { LinkQuestionnaireModalComponent } from 'src/app/shared/consult-forms/dry-eye-forms/dry-eye-medical-history-form/link-assessment-to-questionnaire-modal/link-assessment-to-questionnaire-modal.component';
import { MedicalHistoryRiskSummaryWithDetail } from 'src/app/shared/consult-forms/dry-eye-forms/dry-eye-medical-history-form/medical-history-risk-factor.schema';
import { PatientModalComponent } from 'src/app/shared/consult-forms/patient-modal/patient-modal.component';
import { DryEyeFormsService } from 'src/app/shared/consult-forms/dry-eye-forms/dry-eye-forms.service';
import { MedicalHistoryRiskSummaryService } from 'src/app/shared/consult-forms/dry-eye-forms/dry-eye-medical-history-form/medical-history-risk-summary.service';

// Medical-history AI summary defaults + user-data check (ported verbatim from
// the legacy dry-eye-forms component so the new flat layout reproduces the
// same Generate-button visibility and snapshot-restore behavior).
const EMPTY_MEDICAL_HISTORY_SUMMARY: MedicalHistoryRiskSummaryWithDetail = {
  high: [],
  moderate: [],
  low: [],
  hasAnyRisk: false,
  clinicalContext: { primary: [], fitzpatrick: [] }
};

const EMPTY_MEDICAL_HISTORY_FORM_VALUE: DryEyeMedicalHistoryFormGroupValue = {};

const MEDICAL_HISTORY_DEFAULT_VALUES: { [key: string]: any } = {
  eyeItchiness: 0,
  eyeTearingQuantity: -10,
  symptomFrequency: 0,
  symptomSeverity: 0,
  medicalConditions: true,
  environmentalSymptoms: true,
  otherConditions: true
};

const MEDICAL_HISTORY_IGNORED_KEYS: ReadonlySet<string> = new Set([
  'schemaVersion',
  'medicalHistorySummaryGenerated'
]);

function medicalHistoryFormHasUserData(value: { [key: string]: any } | null | undefined): boolean {
  if (!value) {
    return false;
  }
  for (const key of Object.keys(value)) {
    if (MEDICAL_HISTORY_IGNORED_KEYS.has(key)) {
      continue;
    }
    const controlValue = value[key];
    if (controlValue === null || controlValue === undefined || controlValue === '') {
      continue;
    }
    if (Array.isArray(controlValue) && controlValue.length === 0) {
      continue;
    }
    if (controlValue === MEDICAL_HISTORY_DEFAULT_VALUES[key]) {
      continue;
    }
    return true;
  }
  return false;
}

interface RouteData {
  assessment: Assessment;
}

interface ParentRouteData {
  loggedInDoctor: Doctor;
  patient: Patient;
}

export type AssessmentRouteData = RouteData & ParentRouteData;

@Component({
  selector: 'csi-assessment',
  templateUrl: './assessment.component.html',
  styleUrls: ['./assessment.component.scss'],
  providers: [
    TreatmentFiltersCDSService,
    EConsultFormGroup,
    SymptomValidatorsService
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AssessmentComponent extends ComponentCanDeactivate
  implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('doctorPicker', { static: true }) doctorPicker: ChooseDoctorFromClinicComponent;
  @ViewChild('signatureCanvas', { static: true }) signatureCanvasEl!: ElementRef<HTMLCanvasElement>;
  @ViewChild('assessmentContent', { static: true }) assessmentContentEl: ElementRef<HTMLElement>;
  @Input() onPatientFileSaved$: Subject<Object>;

  public doctorNotesPanelOpen = false;
  public hoverMenu = new HoverMenuHelper();

  public openDoctorNotesPanel() {
    this.doctorNotesPanelOpen = true;
    this.changeRef.markForCheck();
  }

  public closeDoctorNotesPanel() {
    this.doctorNotesPanelOpen = false;
    this.changeRef.markForCheck();
  }

  public readonly AssessmentRequestType = AssessmentRequestType;
  public readonly ButtonAppearances = ButtonAppearances;
  public readonly symptomModes = SymptomModes;

  public dewsIIISignsToShow: string[] | null = null;

  // Method selection card data
  public methodCardMeta: {
    [key: string]: { icon: string; description: string };
  } = {
    [SymptomModes.PreOpScreening]: {
      icon: 'assignment',
      description: 'Baseline evaluation of ocular surface prior to refractive or cataract surgery.'
    },
    [SymptomModes.PreOp]: {
      icon: 'local_hospital',
      description: 'ASCRS pre-operative dry eye assessment for surgical candidates.'
    },
    [SymptomModes.Simple]: {
      icon: 'flash_on',
      description: 'A rapid triage screen for high-volume clinical settings.'
    },
    [SymptomModes.Silver]: {
      icon: 'speed',
      description: 'Accelerated dry eye assessment with streamlined workflow.'
    },
    [SymptomModes.Advanced]: {
      icon: 'verified',
      description: 'Comprehensive diagnostic assessment using validated clinical benchmarks.'
    },
    [SymptomModes.Treatment]: {
      icon: 'healing',
      description: 'Track treatment response and monitor changes over time.'
    },
    [SymptomModes.FollowUp]: {
      icon: 'autorenew',
      description:
        'A focused version for subsequent check-ups. Compares current findings with prior assessments.'
    },
    [SymptomModes.DewsIII]: {
      icon: 'biotech',
      description: 'TFOS DEWS III evidence-based diagnostic methodology for dry eye disease.'
    }
  };
  public assessment: Assessment;
  public patientSignDistribution: { [key: string]: number } = {};
  private canvas?: fabric.Canvas;
  private logEntries: LogEntry[] = [];

  public todayDate = new Date();

  public assessmentCreateAtDateFormControl = new FormControl(new Date().toISOString());

  public cameraAccessEnabled = this.staffService.isStaffPartOfGroup(
    this.staffService.staff || { cognitoGroups: null },
    CognitoGroup.CameraAccess
  );

  public adminAccessEnabled = this.staffService.isStaffPartOfGroup(
    this.staffService.staff || { cognitoGroups: null },
    CognitoGroup.Admin
  );

  public readonly assessmentRouteData$: Observable<AssessmentRouteData> = combineLatest([
    this.route.data,
    this.route.parent.data
  ] as Observable<RouteData | ParentRouteData>[]).pipe(
    map(([routeData, parentRouteData]) => {
      return { ...(routeData as RouteData), ...(parentRouteData as ParentRouteData) };
    })
  );

  public clinicId: string;
  public isPatientAppActive$: Observable<boolean>;
  private assessmentChange$ = new ReplaySubject<Assessment | null>(1);
  public chooseDoctorPrepended$: Observable<string> = this.route.url.pipe(
    map(() => (this.isOnNewEncounterRoute ? 'Assign to:' : 'Assigned to:'))
  );

  public assessment$: Observable<Assessment> = this.route.data.pipe(
    map((data: RouteData) => data.assessment),
    tap(assessment => {
      this.assessment = assessment;
      this.assessmentChange$.next(assessment);
      this.assessmentCreateAtDateFormControl.setValue(
        (assessment && assessment.createdAt) || this.todayDate.toISOString()
      );
      this.updateFromAssessment(assessment);
      this.computeDewsPreviousAssessment();
      this.updateEnrollmentState();
    }),
    shareReplay(1)
  );

  public loggedInDoctor$: Observable<Doctor> = this.route.parent.data.pipe(
    map((data: ParentRouteData) => data.loggedInDoctor),
    tap(loggedInDoctor => {
      this.doctor = loggedInDoctor;
      this.pdfExportService.preloadClinicLogo(this.doctor.clinic);
    }),
    shareReplay(1)
  );

  public doctor: Doctor;

  public isViewingMode = false;
  public shouldHideResponseForm = false;
  public isAssessmentFormPatched = false;
  public showSeemaButton$ = combineLatest([
    this.loggedInDoctor$,
    this.formGroup.assessmentMethod
  ]).pipe(
    map(() => this.staffService.isStaffPartOfGroup(this.doctor, CognitoGroup.CanReferToSeema))
  );

  public showImageRequiredError = false;
  public isFirstEncounter = true;
  public assessmentMethod = null;
  public assessmentMethodChange = null;
  private previousAssessmentMethod: SymptomModes = null;

  public isPatientMissingEnrollmentFields = false;
  public missingEnrollmentFieldsMessage = '';
  public shouldShowEnrollButton = false;
  public isPreOp = false;

  public _isLocked: boolean;

  /** Previous assessment's dryEyeForm — used for DEWS III trend arrows */
  public dewsPreviousDryEyeForm: { [key: string]: any } | null = null;
  /** Date of the previous assessment — displayed in the DEWS III legend */
  public dewsPreviousAssessmentDate: Date | null = null;

  // Step management
  public sections: AssessmentSection[] = [];
  public sectionKeyMap: { [key: string]: boolean } = {};
  public currentStep = 0;
  private scrollSpyObserver: IntersectionObserver | null = null;
  private scrollBottomListener: (() => void) | null = null;
  public showImpressionsWarning$: Observable<boolean> = of(false);
  public onQuestionnaireLinked$: Subject<QuestionnaireRequest[]> = new Subject<
    QuestionnaireRequest[]
  >();

  private _requestingDoctor: Doctor;
  private get requestingDoctor(): Doctor {
    return this._requestingDoctor ? this._requestingDoctor : this.doctor;
  }

  private _isLoadingRefCount = 0;
  get isLoading(): boolean {
    return this._isLoadingRefCount > 0;
  }
  set isLoading(value: boolean) {
    if (value || this._isLoadingRefCount > 0) {
      this._isLoadingRefCount += value ? 1 : -1;
    }
    this.changeRef.markForCheck();
  }
  public autoSaving = false;

  private lockedSnackBarRef: MatSnackBarRef<SimpleSnackBar> = null;

  private logger = new Logger('Assessment');
  clinicDoctors: Partial<Doctor>[] = [];
  signedDoctor: Partial<Doctor>;

  constructor(
    public formGroup: EConsultFormGroup,
    public referralService: ReferralService,
    public referralFormService: ReferralFormService,
    private patientService: PatientService,
    private staffService: StaffService,
    public staffPermissionsService: StaffPermissionsService,
    private clinicService: ClinicService,
    private clinicSetupService: ClinicSetupService,
    private route: ActivatedRoute,
    private router: Router,
    private dialog: MatDialog,
    private assessmentFormService: AssessmentFormService,
    private errorHandlerService: ErrorHandlerService,
    private pdfExportService: PDFExportService,
    private snackBar: MatSnackBar,
    private formErrorScrollingService: FormErrorScrollingService,
    private hostElementRef: ElementRef<HTMLElement>,
    private location: Location,
    public assessmentService: AssessmentService,
    private phonePipe: PhonePipe,
    private changeRef: ChangeDetectorRef,
    private preOpService: PreOpService,
    private pdfSummaryService: PdfSummaryService,
    private html2PdfService: Html2PdfService,
    private fabricService: FabricService,
    private navigationService: NavigationService,
    private appStateService: AppStateService,
    private assessmentPdfSendService: AssessmentPdfSendService,
    private pampIframeService: PampIframeService,
    private customPdfExportService: CustomPdfExportService,
    private transloco: TranslocoService,
    public layoutsService: LayoutsService,
    public schemaService: SchemaService,
    public dryEyeFormsService: DryEyeFormsService,
    private confirmModalService: ConfirmModalService,
    private questionnaireRequestService: QuestionnaireRequestService,
    private medicalHistoryRiskSummaryService: MedicalHistoryRiskSummaryService,
    private subscriptionPlanAccessService: SubscriptionPlanAccessService
  ) {
    super();
    this.isPatientAppActive$ = this.appStateService.isPatientAppActive$;
    const defaultShouldReuse = this.router.routeReuseStrategy.shouldReuseRoute;
    this.router.routeReuseStrategy.shouldReuseRoute = (future, curr) => {
      if (future.component === AssessmentComponent && curr.component === AssessmentComponent) {
        return true;
      }
      return defaultShouldReuse(future, curr);
    };
  }

  // AI Risk Summary (quick-scan) — same Form/AI Summary toggle behavior as the
  // legacy dry-eye-forms component (PR #738). View binds via the persisted
  // snapshot stored on the medicalHistorySummaryGenerated form control;
  // Generate / Regenerate run the pipeline once and write the bundle back.
  public medicalHistorySummary$: Observable<MedicalHistoryRiskSummaryWithDetail>;
  public medicalHistoryFormValue$: Observable<DryEyeMedicalHistoryFormGroupValue>;
  public medicalHistoryRevealTrigger$ = new ReplaySubject<void>(1);
  // Fired by handleSnapshotRestored on saved-encounter reload. ReplaySubject(1)
  // (not a plain Subject) is required so the quick-scan child receives the
  // signal whether it subscribes before or after the snapshot is restored —
  // the DEWSIII section's template branch mounts on a setTimeout after
  // assessmentMethod resolves, well after handleSnapshotRestored fires.
  public medicalHistoryRestoreTrigger$ = new ReplaySubject<void>(1);
  public showMedicalHistorySummary = false;
  public medicalHistorySummaryGenerated = false;
  public formHasMedicalHistoryData = false;
  // Mirrors clinicSetup.generateAiMedicalHistory — when false, the entire AI
  // Summary UI (Generate button, toggle, quick-scan view) is hidden. Updated
  // reactively via the subscription in setupMedicalHistoryRiskSummary so a
  // delayed clinic-setup patch still enables the feature on the fly.
  public isAiMedicalHistoryEnabled: boolean = false;
  public readonly medicalHistorySegments: ReadonlyArray<{
    key: 'form' | 'summary';
    icon: string;
    label: string;
    hasLiveDot: boolean;
  }> = [
    { key: 'form', icon: 'medical_history_form', label: 'Form', hasLiveDot: false },
    { key: 'summary', icon: 'medical_history_summary', label: 'AI Summary', hasLiveDot: true }
  ];

  private setupMedicalHistoryRiskSummary(): void {
    // Mirror the clinic-setup feature flag — UI gates on this. Subscribed
    // (not just read once) so a late patchValue from the clinic-setup API
    // load still flips the feature on without requiring a page reload.
    const generateAiControl = this.clinicSetupService.clinicSetupFormGroup.controls
      .generateAiMedicalHistory;
    this.isAiMedicalHistoryEnabled = !!generateAiControl.value;
    generateAiControl.valueChanges.pipe(untilDestroyed(this)).subscribe(value => {
      this.isAiMedicalHistoryEnabled = !!value;
      this.changeRef.markForCheck();
    });

    const medicalHistoryFormGroup = this.formGroup.dryEyeFormControls.medicalHistoryForm;
    const summarySnapshotControl = medicalHistoryFormGroup.controls.medicalHistorySummaryGenerated;

    // Single source of truth for the AI Summary VIEW: the persisted snapshot
    // on the medicalHistorySummaryGenerated form control. Emits on Generate/
    // Regenerate (setValue) and on saved-encounter reload.
    const savedSnapshot$: Observable<MedicalHistorySummarySnapshot | null> = summarySnapshotControl.valueChanges.pipe(
      startWith(summarySnapshotControl.value),
      shareReplay(1)
    );

    this.medicalHistorySummary$ = savedSnapshot$.pipe(
      map(snapshot => this.normalizeSavedMedicalHistorySummary(snapshot)),
      shareReplay(1)
    );

    this.medicalHistoryFormValue$ = savedSnapshot$.pipe(
      map(snapshot =>
        snapshot && snapshot.formValueSnapshot
          ? snapshot.formValueSnapshot
          : EMPTY_MEDICAL_HISTORY_FORM_VALUE
      ),
      shareReplay(1)
    );

    // Generate-button visibility flag — true when the medical history form
    // contains any value beyond constructor defaults (works for user input AND
    // programmatic patches by the resolver / linked questionnaires).
    merge(of(null), medicalHistoryFormGroup.valueChanges, medicalHistoryFormGroup.statusChanges)
      .pipe(
        map(() => medicalHistoryFormHasUserData(medicalHistoryFormGroup.value)),
        untilDestroyed(this)
      )
      .subscribe(hasData => {
        this.formHasMedicalHistoryData = hasData;
      });

    // Restore handler — when the saved snapshot flips from null to non-null,
    // mirror the boolean flag, switch to the AI Summary tab, and fire the
    // restore trigger so the quick-scan child plays its restore animation.
    // On encounter reload the resolver patches the snapshot before the
    // section template branch mounts; the ReplaySubject(1) buffers the
    // signal so the child still receives it whenever it subscribes.
    const handleSnapshotRestored = (snapshot: MedicalHistorySummarySnapshot | null): void => {
      if (snapshot && !this.medicalHistorySummaryGenerated) {
        this.medicalHistorySummaryGenerated = true;
        this.showMedicalHistorySummary = true;
        // Stream-based path — fires regardless of when the quick-scan child
        // mounts. ReplaySubject(1) replays to late subscribers, so this works
        // whether the section is already in the DOM or mounts seconds later.
        this.medicalHistoryRestoreTrigger$.next();
      }
    };

    summarySnapshotControl.valueChanges
      .pipe(untilDestroyed(this))
      .subscribe(handleSnapshotRestored);

    // Synchronous check for the form-was-patched-BEFORE-ngOnInit case.
    if (summarySnapshotControl.value) {
      handleSnapshotRestored(summarySnapshotControl.value as MedicalHistorySummarySnapshot);
    }
  }

  public generateMedicalHistorySummary(): void {
    this.showMedicalHistorySummary = true;
    this.captureAndStoreMedicalHistorySnapshot();
  }

  public regenerateMedicalHistorySummary(): void {
    this.showMedicalHistorySummary = true;
    this.captureAndStoreMedicalHistorySnapshot();
  }

  private normalizeSavedMedicalHistorySummary(
    snapshot: MedicalHistorySummarySnapshot | null
  ): MedicalHistoryRiskSummaryWithDetail {
    if (!snapshot || !snapshot.summary || typeof snapshot.summary !== 'object') {
      return EMPTY_MEDICAL_HISTORY_SUMMARY;
    }
    const savedSummary = snapshot.summary as any;
    const savedClinicalContext = savedSummary.clinicalContext;
    return {
      high: Array.isArray(savedSummary.high) ? savedSummary.high : [],
      moderate: Array.isArray(savedSummary.moderate) ? savedSummary.moderate : [],
      low: Array.isArray(savedSummary.low) ? savedSummary.low : [],
      hasAnyRisk: !!savedSummary.hasAnyRisk,
      clinicalContext: {
        primary: Array.isArray(savedClinicalContext && savedClinicalContext.primary)
          ? savedClinicalContext.primary
          : [],
        fitzpatrick: Array.isArray(savedClinicalContext && savedClinicalContext.fitzpatrick)
          ? savedClinicalContext.fitzpatrick
          : []
      }
    };
  }

  // Runs the full risk-evaluation pipeline ONCE against the current form value,
  // bundles the result with a snapshot of the form value, writes the bundle to
  // the medicalHistorySummaryGenerated control, and fires the reveal trigger.
  private captureAndStoreMedicalHistorySnapshot(): void {
    const medicalHistoryFormGroup = this.formGroup.dryEyeFormControls.medicalHistoryForm;
    const currentFormValue = medicalHistoryFormGroup.value as DryEyeMedicalHistoryFormGroupValue;
    this.medicalHistoryRiskSummaryService
      .buildMedicalHistoryRiskSummaryWithDetail$(of(currentFormValue))
      .pipe(skip(1), take(1))
      .subscribe(computedSummary => {
        const snapshot: MedicalHistorySummarySnapshot = {
          summary: computedSummary,
          formValueSnapshot: currentFormValue
        };
        // Flip BEFORE setValue so handleSnapshotRestored no-ops via its
        // `!medicalHistorySummaryGenerated` guard — Generate goes through the
        // reveal animation path, not the load-from-saved path.
        this.medicalHistorySummaryGenerated = true;
        medicalHistoryFormGroup.controls.medicalHistorySummaryGenerated.setValue(snapshot);
        this.medicalHistoryRevealTrigger$.next();
      });
  }

  ngOnInit() {
    this.appStateService.currentPatient$.pipe(untilDestroyed(this)).subscribe(() => {
      this.updateEnrollmentState();
      this.changeRef.markForCheck();
    });

    this.isPatientAppActive$
      .pipe(
        distinctUntilChanged(),
        switchMap(isActive => {
          if (!isActive) {
            return of(null);
          }
          return combineLatest([this.appStateService.currentPatient$, this.assessmentChange$]).pipe(
            map(([patient, assessment]) => {
              if (!patient || !assessment || this.isPreOp) {
                return 'none';
              }
              if (this.shouldShowEnrollButton) {
                if (!this.isPatientMissingEnrollmentFields) {
                  return !patient.patientAppId ? 'eligible_enroll' : 'eligible_reenroll';
                }
                return 'missing_fields';
              } else if (patient.patientAppId) {
                return 'enrolled';
              }
              return 'none';
            })
          );
        }),
        filter(state => !!state),
        distinctUntilChanged(),
        skip(1),
        untilDestroyed(this)
      )
      .subscribe(state => {
        if (state === 'enrolled') {
          this.snackBar.open(this.transloco.translate('Patient enrolled successfully'), '', {
            duration: 3000
          });
        }
      });

    this.transloco.langChanges$.pipe(untilDestroyed(this)).subscribe(() => {
      this.updateEnrollmentState();
    });

    this.appStateService.clinic$
      .pipe(
        filter(clinic => !!clinic && !!clinic.clinicSetup),
        untilDestroyed(this)
      )
      .subscribe(clinic => {
        const rawLayouts = clinic.clinicSetup.layouts;
        const layouts = Array.isArray(rawLayouts) ? rawLayouts : [];
        const dewsLayout = layouts.find(l => !!l && l.id === SymptomModes.DewsIII);
        this.dewsIIISignsToShow =
          dewsLayout && dewsLayout.signsLayout ? dewsLayout.signsLayout.show : null;
        this.changeRef.markForCheck();
      });

    if (this.formGroup.controls.apiLogs && this.formGroup.controls.apiLogs.value != null) {
      const logs = this.formGroup.controls.apiLogs.value;
      this.logEntries = [...logs.logEntries];
    }

    this.navigationService.sidebarHidden = true;
    this.navigationService.updateSideNavSchema(assessmentSideNavSchema);

    this.setupMedicalHistoryRiskSummary();
    this.formGroup.assessmentMethod.subscribe(value =>
      setTimeout(() => {
        this.assessmentMethodChange = value;
        this.buildSections(value);
        this.syncSectionStatuses();
        this.navigateToFinalIfComplete();
        this.updateEnrollmentState();
        this.changeRef.markForCheck();
      })
    );

    this.formGroup.valueChanges.subscribe(() => this.changeRef.markForCheck());

    this.formGroup.valueChanges
      .pipe(debounceTime(5000))
      .subscribe(
        () =>
          this.formGroup.dirty &&
          this.assessmentMethod !== null &&
          this.assessmentMethod !== 'preop' &&
          this.autoSave()
      );

    this.formGroup.valueChanges.subscribe(() => this.changeRef.markForCheck());

    this.preOpService.assessmentSaved$.subscribe(() => {
      this.onSave();
    });

    this.assessment$.pipe(untilDestroyed(this)).subscribe();
    this.loggedInDoctor$.pipe(untilDestroyed(this)).subscribe();

    this.onPatientFileSaved$
      .pipe(debounceTime(2000), untilDestroyed(this))
      .subscribe(() => this.autoSave());

    this.interactionSignoffGroup.controls.doctorSigning.valueChanges
      .pipe(untilDestroyed(this))
      .subscribe((id: string) => this.updateAssignedDoctor(id));

    this.clinicService
      .getClinicStaff(this.clinicSetupService.clinicId)
      .pipe(untilDestroyed(this))
      .subscribe(list => {
        this.clinicDoctors = list.filter(
          s => s.staffType === StaffType.Doctor || this.clinicSetupService.isClinicAdmin
        );
        if (this.interactionSignoffGroup) {
          const currentSigningId = this.interactionSignoffGroup.controls.doctorSigning.value;
          this.updateAssignedDoctor(currentSigningId);
        }
      });
  }

  ngAfterViewInit() {
    this.initializeCanvas();
  }

  get hasFaxNumber$(): Observable<boolean> {
    return this.clinicService.getEMRIntegrationFaxNumber(this.clinicSetupService.clinicId).pipe(
      take(1),
      map(faxNumber => !!faxNumber)
    );
  }

  private initializeCanvas() {
    if (!this.canvas && this.signatureCanvasEl) {
      this.canvas = this.fabricService.createCanvas(this.signatureCanvasEl.nativeElement, {
        width: 550,
        height: 150
      });

      if (this.interactionSignoffGroup && this.interactionSignoffGroup.value.drawnSignature) {
        this.fabricService.loadSignature(
          this.canvas,
          this.interactionSignoffGroup.value.drawnSignature
        );
      }
    }
  }

  private updateAssignedDoctor(doctorId: string) {
    this.signedDoctor = this.clinicDoctors.find(d => d.id === doctorId) || null;

    if (!this.signedDoctor) {
      this.signedDoctor = this.doctor;
    }
    this.changeRef.markForCheck();
  }

  @HostListener('document:keydown.control.s', ['$event'])
  @HostListener('document:keydown.meta.s', ['$event'])
  onSaveKeyDown(event: KeyboardEvent) {
    event.preventDefault();
    this.onSave();
  }

  _canDeactivate(): boolean {
    this.logger.debug(this.formGroup);
    return !this.formGroup.dirty;
  }

  ngOnDestroy() {
    this.navigationService.sidebarHidden = false;
    this.snackBar.dismiss();
    if (this.canvas) {
      this.fabricService.destroyCanvas(this.canvas);
    }
    this.teardownScrollSpy();
    this.hoverMenu.destroy();
  }

  public goBack() {
    this.router.navigate(['.', { outlets: { [EncountersOutletName]: null } }], {
      relativeTo: this.route.parent
    });
  }

  public onSave() {
    if (!this.staffPermissionsService.permissions.canEditAssessment) {
      this.openSnackBar(
        this.transloco.translate('You do not have permission to edit this assessment'),
        { duration: 4000 }
      );
      return;
    }
    this.save().subscribe(() => {
      this.openSnackBar(this.transloco.translate('SNACKBARS.SAVED'), { duration: 2000 });
    });
  }

  public autoSave() {
    if (!this.staffPermissionsService.permissions.canEditAssessment) {
      return;
    }
    this.autoSaving = true;
    this.save().subscribe(() => {
      this.autoSaving = false;
      this.openSnackBar(this.transloco.translate('SNACKBARS.AUTO_SAVED'), { duration: 2000 });
    });
  }

  public save = (): Observable<Assessment> => {
    // Single enforcement point for writing an assessment: every save button, the
    // autosave timer, every [saveFn] child binding and openPatientApp() route through here.
    if (!this.staffPermissionsService.permissions.canEditAssessment) {
      return EMPTY;
    }
    this.logger.debug('saving assessment');
    this.isLoading = true;

    this.formGroup.controls.expansionStateFormControl.setValue(this.preOpService.expansionMap);
    this.formGroup.controls.enabledStateFormControl.setValue(this.preOpService.enabledMap);

    if (this.logEntries.length > 0) {
      const apiFormControlValue: APIResponse = this.formGroup.controls.apiLogs.value;

      const APILogs: APIResponse = {
        patientId: this.patient.id,
        assessmentId: this.assessment.id,
        logEntries: [
          ...this.logEntries,
          ...((apiFormControlValue && apiFormControlValue.logEntries) || [])
        ]
      };
      this.formGroup.controls.apiLogs.setValue(APILogs);
    }
    const formGroupAssessmentBody: AssessmentBody = this.formGroup.getRawValue() as AssessmentBody;

    this.assessmentFormService.isAssessmentLocked = this.isLocked;

    const saveAssessment$ = this.assessmentFormService
      .saveAssessment(
        this.formGroup.controls.id.value,
        this.formGroup.assessmentType,
        formGroupAssessmentBody,
        this.formGroup.attachments,
        this.patient,
        (this.doctorPicker.getSelectedDoctor() as Doctor) || this.staffService.staff,
        this.assessmentCreateAtDateFormControl.value || new Date().toISOString(),
        this.isLocked
      )
      .pipe(shareReplay(1));

    saveAssessment$.subscribe(
      assessment => {
        if (!this.assessment) {
          this.patientService.assessments = [assessment, ...this.patientService.assessments];

          this.router.navigate(['.', { outlets: { [EncountersOutletName]: assessment.id } }], {
            relativeTo: this.route.parent,
            replaceUrl: true
          });
        } else {
          const index = this.patientService.assessments.findIndex(
            assessmentToFind => assessmentToFind.id === assessment.id
          );

          this.patientService.assessments[index] = {
            ...this.patientService.assessments[index],
            ...assessment
          };
        }
        this.assessment = assessment;
        this.assessmentService.currentAssessment = assessment;
        this.formGroup.controls.id.setValue(assessment.id);
        this.logger.debug('saved assessment');
        this.formGroup.markAsPristine();
        this.isLoading = false;
        this.isLocked = !!assessment.locked;
        this.updateEnrollmentState();
        this.assessmentChange$.next(assessment);
        this.patientService.syncPatientAppStatus(this.patient.id).subscribe();
      },
      error => {
        this.isLoading = false;
        this.errorHandlerService.handleGraphQLError(error, true);
      }
    );

    return saveAssessment$;
  };

  private saveIfNew(): Observable<{ id: string }> {
    return this.assessment ? of({ id: this.formGroup.controls.id.value }) : this.save();
  }

  openImageWizardModal() {
    this.saveIfNew().subscribe(data => {
      SignImageWizardModalComponent.open(
        this.dialog,
        this.formGroup.dryEyeFormControls.dryEyeForm,
        data.id,
        this.save
      );
    });
  }

  openMobileImageWizardModal() {
    this.saveIfNew().subscribe(data => {
      const path = `${location.origin}/${data.id}/mobile-media-wizard`;
      QrCodeModalComponent.open(this.dialog, {
        qrCodePath: path,
        modalTitle: this.transloco.translate('Scan to open Media Wizard on Mobile')
      });
    });
  }

  requestMediaFromApp() {
    this.saveIfNew().subscribe(() => {
      this.dialog.open(RealtimeMediaModalComponent, {
        data: {
          attachmentFormControl: this.formGroup.dryEyeFormControls.assessmentForm.controls.images
        }
      });
    });
  }

  referAssessment() {
    this.formGroup.submitted = true;

    if (this.formGroup.isAssessmentValid) {
      this.isLoading = true;

      this.save()
        .pipe(
          mergeMap(assessment => {
            this.isLoading = false;

            // Renaming the informationForms to match referral
            let body: any = assessment.body;
            ['patient', 'clinic', 'doctor'].forEach(key => {
              body = body.replace(`${key}InformationForm`, key);
            });

            body = JSON.parse(body);

            body.doctor = {
              firstName: body.doctor.firstName,
              lastName: body.doctor.lastName,
              id: body.doctor.id
            };
            body['diseaseArea'] = this.referralService.dryEyeSpecializedDiseaseArea;

            if (assessment.attachments && assessment.attachments.length > 0) {
              const attachments = [...assessment.attachments];
              attachments.forEach(attachment => delete attachment['__typename']);
              body['assessment'] = assessment;
            }

            body['receiveUpdateNotifications'] = true;
            body['updateNotificationRecipients'] = [this.doctor.email];

            const createReferralInput: CreateReferralInput = {
              referringClinicId: this.doctor.clinic.id,
              referralCenterClinicId: this.referralService.linkedReferralCenter
                .referralCenterClinicId,
              referralReferralCenterId: this.referralService.linkedReferralCenter.id,
              referralPatientId: this.patient.id,
              body: JSON.stringify(body),
              priority: Priority.ROUTINE,
              status: 'New Referral',
              createdAt: assessment.createdAt
            };

            return this.referralFormService.createReferralFromInput(createReferralInput);
          })
        )
        .subscribe(() => {
          this.openSnackBar(
            this.transloco.translate('ASSESSMENT.SNACKBARS.REFERRAL_SENT', {
              referralCenterName: this.referralService.linkedReferralCenter.name
            }),
            {
              duration: 4500
            }
          );
          this.isLoading = false;
        });
    } else {
      this.formErrorScrollingService.scrollToFirstInvalidControlInView(this.hostElementRef);
    }
  }

  public onGeneratePDF({
    pdfType,
    lang,
    destination
  }: {
    pdfType: PDFType;
    lang: string;
    destination: ExportDestination;
  }) {
    // Without this 1 cycle delay the mat-menu does not close
    // properly.
    setTimeout(() => {
      if (
        destination === ExportDestinations.Email ||
        destination === ExportDestinations.PatientApp
      ) {
        this.sendAssessmentToPatient(pdfType, lang, destination);
        return;
      }
      this.generatePDF(pdfType, lang);
    });
  }

  public sendAssessmentToPatient(
    pdfType: PDFType,
    lang: string,
    destination: ExportDestination = ExportDestinations.Email
  ) {
    this.assessmentPdfSendService
      .confirmSend(this.patient, destination)
      .subscribe(confirmed => {
        if (!confirmed) {
          return;
        }
        this.executeSendAssessmentToPatient(pdfType, lang, destination);
      });
  }

  private executeSendAssessmentToPatient(
    pdfType: PDFType,
    lang: string,
    destination: ExportDestination = ExportDestinations.Email
  ) {
    this.isLoading = true;
    this.saveIfNew()
      .pipe(
        concatMap(() =>
          this.assessmentPdfSendService.sendAndNotify({
            assessmentBody: this.getAssessmentBody(),
            clinic: this.requestingDoctor.clinic,
            clinicName: this.clinicSetupService.clinic.name,
            clinicId: this.clinicId,
            assessmentId: this.assessment.id,
            reportType: this.layoutsService.assessmentMethodFriendlyName[
              this.formGroup.dryEyeFormControls.dryEyeForm.controls.assessmentMethod.value
            ],
            patient: this.patient,
            pdfType,
            lang,
            destination
          })
        )
      )
      .subscribe({
        next: () => {
          this.isLoading = false;
        },
        error: () => {
          this.isLoading = false;
        }
      });
  }

  public generatePDF(typeOfPDF: PDFType, lang: string) {
    this.logger.debug('generating pdf');

    this.isLoading = true;

    (!this.isViewingMode ? this.save() : of(null))
      .pipe(
        mergeMap(assessment => {
          if (typeOfPDF === 'custom') {
            this.pdfSummaryService.getSeverityLevel(
              this.getAssessmentBody().dryEyeForm,
              this.getAssessmentBody().dryEyeForm.assessmentMethod
            );
            return this.dialog
              .open(PdfCustomExportComponent, {
                width: '1200px',
                data: {
                  assessmentBody: assessment,
                  patientCharts: cloneDeep(this.customPdfExportService.patientChartImages)
                }
              })
              .afterClosed()
              .pipe(
                map(confirmed => {
                  if (confirmed) {
                    return assessment;
                  }
                  this.isLoading = false;
                  return null;
                })
              );
          }
          return of(assessment);
        })
      )
      .subscribe(assessment => {
        if (!assessment) {
          return;
        }

        this.pdfSummaryService.getSeverityLevel(
          this.getAssessmentBody().dryEyeForm,
          this.getAssessmentBody().dryEyeForm.assessmentMethod
        );

        // Get previous assessment for trend arrows
        const previousAssessmentBody = this.getPreviousAssessmentBody();

        this.pdfExportService
          .downloadPDFForAssessmentBody(
            this.getAssessmentBody(),
            this.requestingDoctor.clinic,
            typeOfPDF,
            this.isDebugMode,
            lang,
            previousAssessmentBody
          )

          .pipe(
            take(1),
            finalize(() => {
              this.isLoading = false;
            })
          )
          .subscribe({
            error: (error: ApolloError) => {
              this.errorHandlerService.handleGraphQLError(error, true);
            }
          });
      });
  }

  private getAssessmentBody(): AssessmentBody {
    const body: AssessmentBody = this.formGroup.getRawValue() as AssessmentBody;
    // TODO: Should probably do this when intial data is loaded in component rather than here
    // See https://gitlab.com/clarity-lv/dry-eye-frontend/issues/196#note_194505229 for more details
    const assignedDoctor = this.doctorPicker.getSelectedDoctor();
    this.assessmentFormService.patchModelsToBody(body, assignedDoctor as Doctor, this.patient);
    body.createdAt = this.assessment.createdAt;
    return body;
  }

  private computeDewsPreviousAssessment(): void {
    const allAssessments = this.patientService.assessments || [];

    // New (unsaved) assessment — this.assessment is undefined.
    // Mirror dry-eye-form.component.ts: use the most recent saved assessment as reference.
    if (!this.assessment) {
      const sorted = [...allAssessments].sort(
        (a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt)
      );
      if (sorted.length === 0) {
        this.dewsPreviousDryEyeForm = null;
        this.dewsPreviousAssessmentDate = null;
        return;
      }
      this.dewsPreviousAssessmentDate = new Date(sorted[0].createdAt);
      try {
        const previousBody: AssessmentBody = JSON.parse(sorted[0].body);
        this.dewsPreviousDryEyeForm = (previousBody && previousBody.dryEyeForm) || null;
      } catch (e) {
        this.dewsPreviousDryEyeForm = null;
      }
      return;
    }

    // Saved assessment — find the most recent assessment before this one.
    const previousBody = this.getPreviousAssessmentBody();
    if (!previousBody) {
      this.dewsPreviousDryEyeForm = null;
      this.dewsPreviousAssessmentDate = null;
      return;
    }
    this.dewsPreviousDryEyeForm = previousBody.dryEyeForm || null;
    this.dewsPreviousAssessmentDate = previousBody.createdAt
      ? new Date(previousBody.createdAt)
      : null;
  }

  private getPreviousAssessmentBody(): AssessmentBody | null {
    const allAssessments = this.patientService.assessments || [];

    if (allAssessments.length === 0) {
      return null;
    }

    const currentCreatedAt = this.assessment.createdAt
      ? Date.parse(this.assessment.createdAt)
      : null;

    // Find assessments before the current one
    const previousAssessments = allAssessments
      .filter(a => {
        if (a.id === this.assessment.id) return false;
        if (currentCreatedAt === null) return true;
        return Date.parse(a.createdAt) < currentCreatedAt;
      })
      .sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt));

    if (previousAssessments.length === 0) {
      return null;
    }

    try {
      const previousAssessment = previousAssessments[0];
      const previousBody: AssessmentBody = JSON.parse(previousAssessment.body);

      // Ensure createdAt is set from the Assessment object if not in body
      if (!previousBody.createdAt && previousAssessment.createdAt) {
        previousBody.createdAt = previousAssessment.createdAt;
      }

      return previousBody;
    } catch (e) {
      console.error('Error parsing previous assessment body:', e);
      return null;
    }
  }

  public get hasAssessmentMethod() {
    return !!this.formGroup.dryEyeFormControls.dryEyeForm.controls.assessmentMethod.value;
  }

  public get patientHasEmail(): boolean {
    return !!this.patient && !!this.patient.email;
  }

  public get patientHasPostalCode(): boolean {
    return !!this.patient && !!this.patient.postalCode;
  }

  public get patientHasDateOfBirth(): boolean {
    return !!this.patient && !!this.patient.dateOfBirth;
  }

  public get isPatientEnrolledInPatientApp(): boolean {
    return !!this.patient && !!this.patient.patientAppId && !this.shouldShowEnrollButton;
  }

  public get isPatientReEnrollEligible(): boolean {
    return !!this.patient && !!this.patient.patientAppId && this.shouldShowEnrollButton;
  }


  // @ts-ignore: public getter and private setter
  public get isLocked() {
    return this._isLocked;
  }

  // @ts-ignore: public getter and private setter
  private set isLocked(isLocked: boolean | null) {
    if (isLocked) {
      this._isLocked = true;

      // this.openLockedSnackBar();

      this.formGroup.disable();
    } else {
      this.closeLockedSnackBar();
      this._isLocked = false;
      if (!this.assessmentService.isViewOnly) {
        this.formGroup.enable();
      }
    }
  }

  private openSnackBar(message: string, config: MatSnackBarConfig = {}, action: string = null) {
    this.closeLockedSnackBar();
    this.snackBar
      .open(message, action, config)
      .afterDismissed()
      .subscribe(() => {
        if (this.isLocked) {
          this.openLockedSnackBar();
        }
      });
  }

  private apiResponseSnackBar(message: string, action: 'Close') {
    this.lockedSnackBarRef = this.snackBar.open(message, action, {
      duration: 2000
    });
  }
  private openLockedSnackBar() {
    if (!this.lockedSnackBarRef) {
      const canUnlock = this.staffPermissionsService.permissions.canFinalizeAssessment;

      this.lockedSnackBarRef = this.snackBar.open(
        this.transloco.translate('SNACKBARS.LOCKED'),
        canUnlock ? this.transloco.translate('SNACKBARS.UNLOCK_ACTION') : null
      );

      if (canUnlock) {
        this.lockedSnackBarRef.onAction().subscribe(() => this.unlockAssessment());
      }
    }
  }

  private closeLockedSnackBar() {
    if (this.lockedSnackBarRef) {
      this.lockedSnackBarRef.dismiss();
      this.lockedSnackBarRef = null;
    }
  }

  public attemptSaveAssessment(): void {
    this.save().subscribe(() => {
      this.openSnackBar(this.transloco.translate('SNACKBARS.SAVED'), { duration: 2000 });
    });
  }

  public openEditPatient(): void {
    PatientModalComponent.open(this.dialog, this.doctor, this.patient, null)
      .afterClosed()
      .pipe(take(1), untilDestroyed(this))
      .subscribe(updatedPatient => {
        if (updatedPatient) {
          this.appStateService.onOperationIntercepted('UpdatePatient', {
            updatePatient: updatedPatient
          });
          this.changeRef.markForCheck();
        }
      });
  }

  public attemptLockAssessment(): void {
    const interactionSignOffForm = this.interactionSignoffGroup;

    const dialogRef = this.dialog.open(InteractionSignoffComponent, {
      data: {
        interactionSignOff: interactionSignOffForm,
        parentFormGroup: this.formGroup
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.formGroup.patchValue({
          interactionSignoffGroup: result
        });
        this.lockAssessment();
      }
    });
  }

  public get interactionSignoffGroup(): InteractionSignOffGroup {
    return this.formGroup.get('interactionSignoffGroup') as InteractionSignOffGroup;
  }

  public lockAssessment(): void {
    if (!this.staffPermissionsService.permissions.canFinalizeAssessment) {
      this.openSnackBar(
        this.transloco.translate('You do not have permission to sign off this assessment'),
        { duration: 4000 }
      );
      return;
    }
    if (this.interactionSignoffGroup && this.interactionSignoffGroup.valid) {
      this.markAllPendingSectionsAsSkipped();
      this.isLocked = true;
      this.save().subscribe();
    }
  }

  private markAllPendingSectionsAsSkipped(): void {
    const progressControl = this.formGroup.dryEyeFormControls.sectionProgress;
    const updated = Object.assign({}, progressControl.value || {});
    for (let i = 0; i < this.sections.length; i++) {
      const section = this.sections[i];
      if (section.isFinal) {
        updated[section.key] = 'done';
      } else if (section.status === 'pending') {
        updated[section.key] = 'done';
      }
    }
    progressControl.setValue(updated);
    this.syncSectionStatuses();
  }

  public unlockAssessment() {
    if (!this.staffPermissionsService.permissions.canFinalizeAssessment) {
      this.openSnackBar(
        this.transloco.translate('You do not have permission to unlock this assessment'),
        { duration: 4000 }
      );
      return;
    }
    this.assessmentService
      .unlockAssessment(this.formGroup.controls.id.value)
      .subscribe(assessment => {
        this.isLocked = assessment.locked;
      });
  }

  public onFax() {
    this.isLoading = true;
    this.clinicService
      .getEMRIntegrationFaxNumber(this.doctor.clinic.id)
      .pipe(
        take(1),
        switchMap(faxNumber => {
          if (faxNumber) {
            return this.faxAssessment(faxNumber);
          }
          return of(null);
        }),
        finalize(() => (this.isLoading = false))
      )
      .subscribe();
  }

  public faxAssessment(to: string): Observable<QueueFaxResult> {
    const formattedPhone: string = this.phonePipe.transform(to);
    this.openSnackBar(this.transloco.translate('SNACKBARS.FAXING', { faxNumber: formattedPhone }));
    return this.pdfExportService
      .faxAssessment(
        formattedPhone,
        this.getAssessmentBody(),
        this.requestingDoctor.clinic as Clinic,
        this.isDebugMode
      )
      .pipe(
        tap((faxResult: QueueFaxResult) => {
          if (faxResult.Status === SRFaxStatus.Success) {
            this.openSnackBar(this.transloco.translate('SNACKBARS.FAX_QUEUED'), {
              duration: 4000
            });
          } else {
            this.errorHandlerService.handleError(`Failed to queue fax to ${formattedPhone}`);
            alert(
              `Failed to enqueue fax to ${formattedPhone}. Issue has been reported to CSI Dry Eye support.`
            );
          }
        })
      );
  }

  private updateFromAssessment(assessment: Assessment) {
    if (assessment) {
      if (
        this.patient.assessments &&
        this.patient.assessments.items &&
        this.patient.assessments.items.length > 0
      ) {
        this.isFirstEncounter =
          this.patient.assessments.items.sort(
            (a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt)
          )[this.patient.assessments.items.length - 1].id === assessment.id;
      }

      this.formGroup.assessmentType = assessment.type || AssessmentType.DryEyeSpecialized;

      const body: AssessmentBody = JSON.parse(assessment.body);
      if (body.medicalHistoryForm) {
        if (body.medicalHistoryForm.medications)
          body.medicalHistoryForm.medications = this.formGroup.normalizeDrugs(
            body.medicalHistoryForm.medications
          );

        const patientEnteredMedication = body.medicalHistoryForm.patientEnteredMedicationsFreeText;
        if (patientEnteredMedication && typeof patientEnteredMedication === 'string') {
          body.medicalHistoryForm.patientEnteredMedicationsFreeText = [patientEnteredMedication];
        }
      }
      this.formGroup.patchValue(body, {}, () => {
        if (AssessmentUtils.hasAssessmentRequests(assessment)) {
          const assessmentRequest = assessment.requests.items[0] as AssessmentRequest;
          this.updateFromAssessmentRequest(assessmentRequest);
          this.changeToViewOnly(false);
        } else {
          this.isAssessmentFormPatched = true;
          if (this.assessmentService.isViewOnly) {
            this.changeToViewOnly(true);
          }
        }

        if (!this.formGroup.entryMethod) {
          this.formGroup.entryMethod = EntryMethod.Manual;
        }
        this.isLocked = assessment.locked;
      });
      this.formGroup.patchAssessmentAttachments(assessment.attachments);
      if (!this.formGroup.manualEntryControls.doctorInformationForm.controls.id.value) {
        this.formGroup.manualEntryControls.doctorInformationForm.controls.id.setValue(
          assessment.doctor.id
        );
      }
      this.clinicId = assessment.clinic.id;
    } else {
      this.isLocked = false;
      this.formGroup.assessmentType = AssessmentType.DryEyeSpecialized;
      this.formGroup.entryMethod = EntryMethod.Manual;
      this.isAssessmentFormPatched = true;

      this.staffService
        .getLoggedInStaff()
        .pipe(untilDestroyed(this))
        .subscribe(doctor => {
          this.formGroup.manualEntryControls.doctorInformationForm.controls.id.setValue(doctor.id);
          this.clinicId = doctor.clinic.id;
        });
    }
  }

  changeToViewOnly(shouldHideResponseForm: boolean) {
    this.isViewingMode = true;
    this.shouldHideResponseForm = shouldHideResponseForm;
    this.changeRef.markForCheck();

    setTimeout(() => {
      this.formGroup.responseFormControls.responseForm.disable();
      this.formGroup.disable();
    });
  }

  openCopyModal() {
    const assessmentWithCurrentData = {
      ...this.assessment,
      body: JSON.stringify(this.formGroup.getRawValue())
    };
    this.dialog.open(CopyToEmrModalComponent, {
      data: {
        showAssessmentList: false,
        assessment: assessmentWithCurrentData,
        assessmentMethod: this.formGroup.dryEyeFormControls.dryEyeForm.controls.assessmentMethod
          .value
      },
      width: '95vw',
      minWidth: '50rem',
      height: '92vh'
    });
  }

  openLinkQuestionnaireModal() {
    const dialogRef = this.dialog.open(LinkQuestionnaireModalComponent, {
      data: { saveFn: this.save }
    });

    dialogRef.afterClosed().subscribe(newlyLinkedQuestionnaires => {
      if (newlyLinkedQuestionnaires) {
        const scoreMap = this.questionnaireRequestService.getLastCompletedQuestionnairesByScore(
          newlyLinkedQuestionnaires
        );
        this.dryEyeFormsService.updateSymptomScores(
          this.formGroup.dryEyeFormControls.dryEyeForm,
          scoreMap
        );
        this.onQuestionnaireLinked$.next(newlyLinkedQuestionnaires);
      }
    });
  }

  onOsdi6QuestionnaireLinked(questionnaire: any): void {
    const scoreMap = this.questionnaireRequestService.getLastCompletedQuestionnairesByScore([
      questionnaire
    ]);
    this.dryEyeFormsService.updateSymptomScores(
      this.formGroup.dryEyeFormControls.dryEyeForm,
      scoreMap
    );
    this.onQuestionnaireLinked$.next([questionnaire]);
  }

  public sendToEmr(): Observable<any> {
    const emrPatientConfig = {
      patientExternalID: this.patient.externalPatientId,
      fileName: `${this.patient.firstName}--${
        this.patient.lastName
      }--${this.pdfExportService.getDateForPDFFilename()}.pdf`,
      documentTitle: 'Detailed Assessment'
    };

    return this.pdfExportService
      .getHtmlForAssessmentBody(
        this.getAssessmentBody(),
        this.requestingDoctor.clinic,
        'detailed',
        false,
        false,
        'en'
      )
      .pipe(
        mergeMap((html: string) => this.html2PdfService.html2Pdf(html)),
        mergeMap(data => {
          emrPatientConfig['base64'] = data.base64;
          return this.assessmentFormService.sendToEmr(emrPatientConfig);
        })
      );
  }

  private updateFailedAPIResponse() {
    this.save().subscribe();
  }

  public lockAndSend() {
    const interactionSignOffForm = this.interactionSignoffGroup;

    const dialogRef = this.dialog.open(InteractionSignoffComponent, {
      data: {
        interactionSignOff: interactionSignOffForm,
        parentFormGroup: this.formGroup
      }
    });
    dialogRef
      .afterClosed()
      .pipe(
        tap(() => (this.isLoading = true)),
        filter(result => result != null),
        switchMap(dialogResult =>
          this.save().pipe(
            switchMap(result =>
              this.sendToEmr().pipe(map(APIresponse => [APIresponse, dialogResult] as const))
            )
          )
        ),
        finalize(() => (this.isLoading = false))
      )
      .subscribe({
        next: ([response, result]) => {
          if (response && response.httpStatus === 200) {
            this.logEntries.push({ timestamp: new Date().toISOString(), message: response });
            this.formGroup.patchValue({
              interactionSignoffGroup: result
            });
            this.lockAssessment();
            this.apiResponseSnackBar('Successfully Uploaded', 'Close');
            return;
          }
          this.logEntries.push({ timestamp: new Date().toISOString(), message: response });
          this.updateFailedAPIResponse();
          this.apiResponseSnackBar('Failed to Upload', 'Close');
        }
      });
  }

  private updateFromAssessmentRequest(assessmentRequest: AssessmentRequest) {
    this.formGroup.patchAssessmentRequestResponse(assessmentRequest);

    this._requestingDoctor = assessmentRequest.doctor as Doctor;
    if (this._requestingDoctor) {
      this.pdfExportService.preloadClinicLogo(this._requestingDoctor.clinic);
    }
    const initialSignerId = this.interactionSignoffGroup.controls.doctorSigning.value;
    this.updateAssignedDoctor(initialSignerId);
    this.isAssessmentFormPatched = true;
  }

  private isFormGroupValid(type: AssessmentRequestType) {
    if (type === AssessmentRequestType.EConsult) {
      const hasImage = this.formGroup.attachments && this.formGroup.attachments.length > 0;
      this.showImageRequiredError = !hasImage;
      this.logger.debug(
        'Checking if E-Consult form has at least one image',
        hasImage,
        this.formGroup.attachments
      );
      return this.formGroup.isAssessmentValid && hasImage;
    } else {
      this.logger.debug('form group is valid - not a manual entry econsult');
      this.showImageRequiredError = false;
      return this.formGroup.isAssessmentValid;
    }
  }

  public get patient(): Patient {
    return this.appStateService.getCurrentPatient();
  }

  public get patientGenderInitial(): string {
    if (!this.patient || !this.patient.gender) {
      return '';
    }
    return this.patient.gender.charAt(0).toUpperCase();
  }

  private get isOnNewEncounterRoute(): boolean {
    return this.route.snapshot.url[0].path === EncountersRoutes.New;
  }

  // Query param, not a route param — '?debug' is how the PDF debug window is opened
  // (see econsult.component.ts). paramMap never matched here, so it was unreachable.
  private get isDebugMode(): boolean {
    return this.route.snapshot.queryParamMap.has('debug');
  }

  private isNutritionBasedTestingHidden(): boolean {
    const flattenedSchema = this.subscriptionPlanAccessService.flattenedSubscriptionPlanSchema;
    const featureEntry = flattenedSchema['features.' + Feature.NUTRITION_BASED_TESTING];
    const accessState = featureEntry && featureEntry.accessState;
    return accessState === AccessState.HIDDEN;
  }

  buildSections(method: SymptomModes): void {
    if (!method) {
      this.sections = [];
      this.sectionKeyMap = {};
      return;
    }

    const allSections = ASSESSMENT_SECTION_SCHEMAS[method];
    if (!allSections) {
      this.sections = [];
      this.sectionKeyMap = {};
      return;
    }

    // Remove nutrition based testing section if it is hidden due subscription plan access control
    const schema = this.isNutritionBasedTestingHidden()
      ? allSections.filter(entry => entry.key !== NUTRITION_BASED_TESTING_SECTION_KEY)
      : allSections;

    const visibleSchema = schema.filter(entry => {
      return (
        !entry.requiredPermission ||
        this.staffPermissionsService.permissions[entry.requiredPermission]
      );
    });

    this.sections = visibleSchema.map(function(entry) {
      return {
        name: entry.name,
        key: entry.key,
        status: 'pending' as 'pending',
        isPreStep: entry.isPreStep,
        isFinal: entry.isFinal
      };
    });

    let keyMap = {};
    for (var i = 0; i < this.sections.length; i++) {
      keyMap[this.sections[i].key] = true;
    }
    this.sectionKeyMap = keyMap;

    this.currentStep = 0;

    if (this.previousAssessmentMethod !== null && this.previousAssessmentMethod !== method) {
      this.formGroup.dryEyeFormControls.sectionProgress.setValue({});
    }
    this.previousAssessmentMethod = method;

    setTimeout(() => this.setupScrollSpy(), 0);
  }

  get visibleSections(): AssessmentSection[] {
    return this.sections;
  }

  onStepChange(stepIndex: number): void {
    if (stepIndex >= 0 && stepIndex < this.sections.length) {
      this.currentStep = stepIndex;
      const section = this.sections[stepIndex];
      if (section) {
        const el = document.getElementById('flat-section-' + section.key);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }
      this.changeRef.markForCheck();
    }
  }

  goToNextStep(): void {
    this.advanceToNextStep();
  }

  goToPrevStep(): void {
    if (this.currentStep > 0) {
      this.currentStep--;
      this.scrollContentToTop();
      this.changeRef.markForCheck();
    }
  }

  completeAndNext(): void {
    this.markSectionStatus('done');
    this.advanceToNextStep();
  }

  skipAndNext(): void {
    this.markSectionStatus('skipped');
    this.advanceToNextStep();
  }

  completeCurrentSection(): void {
    this.markSectionStatus('done');
  }

  private advanceToNextStep(): void {
    if (this.currentStep < this.sections.length - 1) {
      this.currentStep++;
      this.scrollContentToTop();
      this.changeRef.markForCheck();
    }
  }

  private scrollContentToTop(): void {
    if (this.assessmentContentEl) {
      this.assessmentContentEl.nativeElement.scrollTop = 0;
    }
  }

  private setupScrollSpy(): void {
    this.teardownScrollSpy();
    if (!this.assessmentContentEl) {
      return;
    }

    const root = this.assessmentContentEl.nativeElement;

    this.scrollSpyObserver = new IntersectionObserver(
      entries => {
        for (let i = 0; i < entries.length; i++) {
          const entry = entries[i];
          if (entry.isIntersecting) {
            const key = entry.target.id.replace('flat-section-', '');
            for (let j = 0; j < this.sections.length; j++) {
              if (this.sections[j].key === key) {
                this.currentStep = j;
                this.changeRef.markForCheck();
                break;
              }
            }
          }
        }
      },
      {
        root: root,
        rootMargin: '-10% 0px -80% 0px',
        threshold: 0
      }
    );

    const els = root.querySelectorAll('.flat-section');
    for (let k = 0; k < els.length; k++) {
      this.scrollSpyObserver.observe(els[k]);
    }

    let wasAtBottom = false;
    this.scrollBottomListener = () => {
      if (this.sections.length === 0) return;
      const lastIdx = this.sections.length - 1;
      const atBottom = root.scrollTop + root.clientHeight >= root.scrollHeight - 50;

      if (atBottom) {
        wasAtBottom = true;
        if (this.currentStep !== lastIdx) {
          this.currentStep = lastIdx;
          this.changeRef.markForCheck();
        }
      } else if (wasAtBottom) {
        wasAtBottom = false;
        const prevIdx = lastIdx - 1;
        if (prevIdx >= 0 && this.currentStep !== prevIdx) {
          this.currentStep = prevIdx;
          this.changeRef.markForCheck();
        }
      }
    };
    root.addEventListener('scroll', this.scrollBottomListener);
  }

  private teardownScrollSpy(): void {
    if (this.scrollSpyObserver) {
      this.scrollSpyObserver.disconnect();
      this.scrollSpyObserver = null;
    }
    if (this.scrollBottomListener && this.assessmentContentEl) {
      this.assessmentContentEl.nativeElement.removeEventListener(
        'scroll',
        this.scrollBottomListener
      );
      this.scrollBottomListener = null;
    }
  }

  private markSectionStatus(status: 'done' | 'skipped'): void {
    const section = this.sections[this.currentStep];
    if (!section) return;
    const progressControl = this.formGroup.dryEyeFormControls.sectionProgress;
    const currentValue = progressControl.value || {};
    progressControl.setValue(Object.assign({}, currentValue, { [section.key]: status }));
    this.syncSectionStatuses();
  }

  private syncSectionStatuses(): void {
    const progress = this.formGroup.dryEyeFormControls.sectionProgress.value || {};
    for (let i = 0; i < this.sections.length; i++) {
      const val = progress[this.sections[i].key];
      if (val === 'done' || val === true) {
        this.sections[i].status = 'done';
      } else if (val === 'skipped') {
        this.sections[i].status = 'skipped';
      } else {
        this.sections[i].status = 'pending';
      }
    }
    this.sections = this.sections.slice();
  }

  private navigateToFinalIfComplete(): void {
    if (this.sections.length === 0) return;
    const nonFinalSections = this.sections.filter(function(s) {
      return !s.isFinal;
    });
    if (nonFinalSections.length === 0) return;
    const allComplete = nonFinalSections.every(function(s) {
      return s.status === 'done' || s.status === 'skipped';
    });
    if (allComplete) {
      this.currentStep = this.sections.length - 1;
    }
  }

  get isDewsIII(): boolean {
    return this.assessmentMethodChange === SymptomModes.DewsIII;
  }

  getSectionStepNum(key: string): number {
    const idx = this.sections.findIndex(s => s.key === key);
    return idx + 1;
  }

  get isFirstVisibleStep(): boolean {
    return this.currentStep === 0;
  }

  get isLastVisibleStep(): boolean {
    return this.sections.length > 0 && this.currentStep === this.sections.length - 1;
  }

  get currentSectionName(): string {
    const section = this.sections[this.currentStep];
    return section ? section.name : '';
  }

  get currentSectionKey(): string {
    const section = this.sections[this.currentStep];
    return section ? section.key : '';
  }

  get currentSectionStatus(): string {
    const section = this.sections[this.currentStep];
    return section ? section.status : 'pending';
  }

  noSort(): number {
    return 0;
  }

  confirmMethodSelection(method: SymptomModes): void {
    this.onMethodChangeFromSidebar(method);
  }

  onMethodChangeFromSidebar(method: SymptomModes): void {
    // Swapping the method rewrites which sections and controls the assessment has, so it is
    // an edit. The sidebar select binds [value] rather than [formControl], so it never
    // inherited the form's disabled state — the write path has to check for itself.
    if (!this.staffPermissionsService.permissions.canEditAssessment) {
      return;
    }

    // Delegate to the assessment method form's onMethodChange logic
    const currentMethod = this.formGroup.dryEyeFormControls.dryEyeForm.controls.assessmentMethod
      .value;

    if (!currentMethod) {
      this.formGroup.dryEyeFormControls.dryEyeForm.controls.assessmentMethod.setValue(method);
      return;
    }

    if (
      (currentMethod !== SymptomModes.PreOp && method === SymptomModes.PreOp) ||
      (currentMethod === SymptomModes.PreOp && method !== SymptomModes.PreOp)
    ) {
      const currentFriendly = this.layoutsService.assessmentMethodFriendlyName[currentMethod];
      const newFriendly = this.layoutsService.assessmentMethodFriendlyName[method];
      const title = this.transloco.translate('dryEyeAssessmentMethodForm.changeMethodTitle', {
        current: currentFriendly,
        new: newFriendly
      });
      const body = this.transloco.translate('dryEyeAssessmentMethodForm.changeMethodBody', {
        current: currentFriendly,
        new: newFriendly
      });
      const yesButton = this.transloco.translate('dryEyeAssessmentMethodForm.overwriteAndChange');
      const noButton = this.transloco.translate('dryEyeAssessmentMethodForm.cancel');

      this.confirmModalService
        .show(title, body, yesButton, noButton)
        .pipe(untilDestroyed(this))
        .subscribe(confirmed => {
          if (confirmed) {
            this.formGroup.dryEyeFormControls.dryEyeForm.controls.assessmentMethod.setValue(method);
          }
        });
      return;
    }

    this.formGroup.dryEyeFormControls.dryEyeForm.controls.assessmentMethod.setValue(method);
  }

  openPatientApp(isDefault = false) {
    if (!this.patient) {
      return;
    }
    this.executeOpenPatientApp(isDefault);
  }

  private executeOpenPatientApp(isDefault = false) {
    const shouldSave =
      this.staffPermissionsService.permissions.canEditAssessment &&
      !this.isViewingMode &&
      !this.isLocked &&
      this.formGroup.dirty;

    const openIframe = () => {
      if (!this.patient.patientAppId) {
        this.pampIframeService.openPatientEnrollment(this.patient.id, isDefault);
      } else if (this.shouldShowEnrollButton) {
        this.pampIframeService.openPatientReEnrollment(this.patient.id, isDefault);
      } else {
        this.pampIframeService.openPatientDashboard(this.patient.id);
      }
    };

    if (shouldSave) {
      this.save().subscribe(openIframe);
    } else {
      openIframe();
    }
  }

  public updateEnrollmentState() {
    this.updateIsPreOpState();
    this.updateMissingEnrollmentFieldsState();
    this.shouldShowEnrollButton = this.computeShouldShowEnrollButton();
  }

  private updateIsPreOpState() {
    this.isPreOp =
      this.hasAssessmentMethod &&
      this.formGroup.dryEyeFormControls.dryEyeForm.controls.assessmentMethod.value ===
        this.symptomModes.PreOp;
  }

  private updateMissingEnrollmentFieldsState() {
    const enrollmentStatus = hasMissingEnrollmentFields(this.patient, this.transloco);
    this.isPatientMissingEnrollmentFields = enrollmentStatus.isMissingFields;
    this.missingEnrollmentFieldsMessage = enrollmentStatus.message;
  }

  private computeShouldShowEnrollButton(): boolean {
    if (!this.assessment || this.isPreOp || !this.patient) {
      return false;
    }

    const patientAppEnrollmentConfig = safeParseJson(this.patient.patientAppEnrollmentConfig);

    if (
      !patientAppEnrollmentConfig ||
      !patientAppEnrollmentConfig.assessmentHistory ||
      !Array.isArray(patientAppEnrollmentConfig.assessmentHistory) ||
      patientAppEnrollmentConfig.assessmentHistory.length === 0
    ) {
      return false;
    }

    const currentAssessmentId = this.assessment.id;
    if (!currentAssessmentId) {
      return true;
    }

    const isInHistory = patientAppEnrollmentConfig.assessmentHistory.some(item => {
      return item && item.id === currentAssessmentId;
    });

    if (isInHistory) {
      return false;
    }

    const currentAssessmentDateStr = this.assessment.createdAt;
    if (!currentAssessmentDateStr) {
      return true;
    }
    const currentAssessmentTime = new Date(currentAssessmentDateStr).getTime();

    const isOlderThanHistory = patientAppEnrollmentConfig.assessmentHistory.some(item => {
      if (!item) return false;
      const itemDateStr = item.createdAt || item.updatedAt;
      if (!itemDateStr) return false;
      const itemTime = new Date(itemDateStr).getTime();
      return currentAssessmentTime < itemTime;
    });

    return !isOlderThanHistory;
  }

  public openMenuOnHover(trigger: MatMenuTrigger): void {
    if (this.isPatientMissingEnrollmentFields) {
      return;
    }
    this.hoverMenu.openMenuOnHover(trigger);
  }

  public closeMenuOnLeave(trigger?: MatMenuTrigger): void {
    this.hoverMenu.closeMenuOnLeave(trigger);
  }

  public cancelMenuClose(): void {
    this.hoverMenu.cancelMenuClose();
  }
}
